import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where, DocumentData, updateDoc, doc, getDoc, addDoc, onSnapshot } from "firebase/firestore";
import { db, auth } from "../../lib/firebase";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { format } from "date-fns";
import { CheckCircle, Calendar, User, Book, Clock, FileX, CalendarCheck } from "lucide-react";

// Helper function to format time to 12-hour format
const formatTime12Hour = (time24: string): string => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

interface Schedule {
  id: string;
  studentId: string;
  courseId: string;
  enrollmentId?: string;
  instructorId: string;
  date: string;
  time: string;
  timeSlotId?: string;
  status: "scheduled" | "completed" | "cancelled" | "rescheduled";
  type: "schedule" | "reschedule";
  createdAt?: any;
  completedAt?: any;
  completionNotes?: string;
  rescheduledAt?: any;
  newScheduleNote?: string;
}

interface ScheduleRequest {
  id: string;
  studentId: string;
  courseId: string;
  enrollmentId?: string;
  scheduleId?: string;
  type: "schedule" | "reschedule";
  status: "pending" | "approved" | "rejected";
  requestedDate?: string;
  newPreferredDate?: string;
  timeSlotId?: string;
  newTimeSlotId?: string;
  preferredTime?: string;
  newPreferredTime?: string;
  notes?: string;
  reason?: string;
  createdAt?: { seconds: number };
  rejectionReason?: string;
  assignedInstructor?: string;
  originalScheduleId?: string;
}

interface Student {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface Course {
  id: string;
  name?: string;
  title?: string;
  duration?: string;
  sessionHours?: number;
  price?: number;
}

interface Instructor {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  branch: string;
  maxStudents: number;
  createdAt?: any;
}

const scheduleStatusOptions = [
  { value: "all", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "rescheduled", label: "Rescheduled" },
];

const requestStatusOptions = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const requestTypeOptions = [
  { value: "all", label: "All" },
  { value: "schedule", label: "Schedule" },
  { value: "reschedule", label: "Reschedule" },
];

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => currentYear - i);
};

const StaffScheduleManagement: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [scheduleRequests, setScheduleRequests] = useState<ScheduleRequest[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [branch, setBranch] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [scheduleStatus, setScheduleStatus] = useState("scheduled");
  const [scheduleMonth, setScheduleMonth] = useState("all");
  const [scheduleYear, setScheduleYear] = useState("all");
  const [requestStatus, setRequestStatus] = useState("pending");
  const [requestMonth, setRequestMonth] = useState("all");
  const [requestYear, setRequestYear] = useState("all");
  const [requestType, setRequestType] = useState("all");
  const [scheduleSearch, setScheduleSearch] = useState("");
  const [requestSearch, setRequestSearch] = useState("");

  // Dialog states for schedules
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completionNotes, setCompletionNotes] = useState("");

  // Dialog states for requests
  const [selectedRequest, setSelectedRequest] = useState<ScheduleRequest | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [assignedInstructor, setAssignedInstructor] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  // Dialog state for viewing student details
  const [showStudentDetailsDialog, setShowStudentDetailsDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const [activeTab, setActiveTab] = useState("schedules");

  useEffect(() => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const unsubscribers: (() => void)[] = [];

    const setupRealtimeListeners = async () => {
      try {
        // Get current staff user's branch
        const userDoc = await getDoc(doc(db, "users", auth.currentUser!.uid));
        const userData = userDoc.data();
        if (userData && userData.branch) {
          const branchDoc = await getDoc(doc(db, "branches", userData.branch));
          const branchData = branchDoc.data();
          setBranch(branchData?.name || userData.branch);
        }

        // Set up real-time listener for schedules
        const schedulesUnsubscribe = onSnapshot(collection(db, "schedules"), (snapshot) => {
          const schedulesData = snapshot.docs.map((doc) => ({ 
            id: doc.id, 
            ...doc.data() 
          })) as Schedule[];
          setSchedules(schedulesData);
        });
        unsubscribers.push(schedulesUnsubscribe);

        // Set up real-time listener for schedule requests
        const scheduleRequestsUnsubscribe = onSnapshot(collection(db, "scheduleRequests"), (snapshot) => {
          const requestsData = snapshot.docs.map((doc) => ({ 
            id: doc.id, 
            ...doc.data(),
            type: "schedule"
          })) as ScheduleRequest[];
          
          // Also listen to reschedule requests
          const rescheduleRequestsUnsubscribe = onSnapshot(collection(db, "rescheduleRequests"), (rescheduleSnapshot) => {
            const rescheduleData = rescheduleSnapshot.docs.map((doc) => ({ 
              id: doc.id, 
              ...doc.data(),
              type: "reschedule"
            })) as ScheduleRequest[];
            
            const allRequests = [...requestsData, ...rescheduleData];
            setScheduleRequests(allRequests);
          });
          unsubscribers.push(rescheduleRequestsUnsubscribe);
        });
        unsubscribers.push(scheduleRequestsUnsubscribe);

        // Set up real-time listener for students
        const studentsUnsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
          const studentsData = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((user: any) => user.role === "student") as Student[];
          setStudents(studentsData);
        });
        unsubscribers.push(studentsUnsubscribe);

        // Set up real-time listener for instructors
        const instructorsUnsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
          const instructorsData = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((user: any) => user.role === "instructor") as Instructor[];
          setInstructors(instructorsData);
        });
        unsubscribers.push(instructorsUnsubscribe);

        // Set up real-time listener for courses
        const coursesUnsubscribe = onSnapshot(collection(db, "courses"), (snapshot) => {
          const coursesData = snapshot.docs.map((doc) => ({ 
            id: doc.id, 
            ...doc.data() 
          })) as Course[];
          setCourses(coursesData);
        });
        unsubscribers.push(coursesUnsubscribe);

        // Set up real-time listener for time slots
        if (userData?.branch) {
          const timeSlotsUnsubscribe = onSnapshot(
            query(collection(db, "timeSlots"), where("branch", "==", userData.branch)),
            (snapshot) => {
              const timeSlotsData = snapshot.docs.map((doc) => ({ 
                id: doc.id, 
                ...doc.data() 
              })) as TimeSlot[];
              setTimeSlots(timeSlotsData);
            }
          );
          unsubscribers.push(timeSlotsUnsubscribe);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error setting up real-time listeners:", error);
        setError("Failed to load data. Please try again.");
        setLoading(false);
      }
    };

    setupRealtimeListeners();

    // Cleanup function to unsubscribe from all listeners
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  // Filtered schedules
  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      const student = students.find((s) => s.id === schedule.studentId);
      const course = courses.find((c) => c.id === schedule.courseId);
      const instructor = instructors.find((i) => i.id === schedule.instructorId);
      
      // Date filtering - use createdAt or default to current date
      const dateField = schedule.createdAt;
      let scheduleDate = new Date();
      if (dateField) {
        if (typeof dateField.seconds === "number") {
          scheduleDate = new Date(dateField.seconds * 1000);
        } else if (dateField instanceof Date) {
          scheduleDate = dateField;
        }
      }
      
      // When status is "all", exclude rescheduled schedules unless specifically requested
      const matchesStatus = scheduleStatus === "all" 
        ? schedule.status !== "rescheduled"
        : schedule.status === scheduleStatus;
      
      const matchesMonth = scheduleMonth === "all" || scheduleDate.getMonth() + 1 === Number(scheduleMonth);
      const matchesYear = scheduleYear === "all" || scheduleDate.getFullYear() === Number(scheduleYear);
      
      const studentName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "";
      const instructorName = instructor ? `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim() : "";
      const courseName = course?.name || course?.title || "";
      
      const matchesSearch = !scheduleSearch ||
        (studentName && studentName.toLowerCase().includes(scheduleSearch.toLowerCase())) ||
        (instructorName && instructorName.toLowerCase().includes(scheduleSearch.toLowerCase())) ||
        (courseName && courseName.toLowerCase().includes(scheduleSearch.toLowerCase())) ||
        (student?.email && student.email.toLowerCase().includes(scheduleSearch.toLowerCase()));
      
      return matchesStatus && matchesMonth && matchesYear && matchesSearch;
    });
  }, [schedules, students, courses, instructors, scheduleStatus, scheduleMonth, scheduleYear, scheduleSearch]);

  // Filtered schedule requests
  const filteredRequests = useMemo(() => {
    return scheduleRequests.filter((request) => {
      const student = students.find((s) => s.id === request.studentId);
      const course = courses.find((c) => c.id === request.courseId);
      
      // Date filtering - use createdAt
      const dateField = request.createdAt;
      let requestDate = new Date();
      if (dateField) {
        if (typeof dateField.seconds === "number") {
          requestDate = new Date(dateField.seconds * 1000);
        } else if (dateField instanceof Date) {
          requestDate = dateField;
        }
      }
      
      const matchesStatus = requestStatus === "all" || request.status === requestStatus;
      const matchesType = requestType === "all" || request.type === requestType;
      const matchesMonth = requestMonth === "all" || requestDate.getMonth() + 1 === Number(requestMonth);
      const matchesYear = requestYear === "all" || requestDate.getFullYear() === Number(requestYear);
      
      const studentName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "";
      const courseName = course?.name || course?.title || "";
      
      const matchesSearch = !requestSearch ||
        (studentName && studentName.toLowerCase().includes(requestSearch.toLowerCase())) ||
        (courseName && courseName.toLowerCase().includes(requestSearch.toLowerCase())) ||
        (student?.email && student.email.toLowerCase().includes(requestSearch.toLowerCase()));
      
      return matchesStatus && matchesType && matchesMonth && matchesYear && matchesSearch;
    });
  }, [scheduleRequests, students, courses, requestStatus, requestType, requestMonth, requestYear, requestSearch]);

  // Helper function to calculate duration in hours from time slot
  const calculateTimeSlotDuration = (timeSlotId: string): number => {
    const timeSlot = timeSlots.find(slot => slot.id === timeSlotId);
    if (!timeSlot) {
      console.warn("Time slot not found for ID:", timeSlotId);
      return 4; // Default fallback
    }

    // Parse time strings (format: "HH:MM")
    const [startHour, startMin] = timeSlot.startTime.split(':').map(Number);
    const [endHour, endMin] = timeSlot.endTime.split(':').map(Number);
    
    // Convert to total minutes
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    // Calculate duration in hours
    const durationMinutes = endMinutes - startMinutes;
    const durationHours = durationMinutes / 60;
    
    return Math.max(durationHours, 0.5); // Minimum 30 minutes
  };

  // Handler functions
  const handleCompleteSchedule = async () => {
    if (!selectedSchedule) {
      setError("No schedule selected.");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Update schedule status to completed
      await updateDoc(doc(db, "schedules", selectedSchedule.id), {
        status: "completed",
        completedAt: new Date(),
        completionNotes: completionNotes.trim() || ""
      });

      // If there's an enrollmentId, check if the course is fully completed
      if (selectedSchedule.enrollmentId) {
        // Get the course details to check total hours and session duration
        const course = courses.find(c => c.id === selectedSchedule.courseId);
        const enrollment = await getDoc(doc(db, "enrollments", selectedSchedule.enrollmentId));
        const enrollmentData = enrollment.data();
        
        if (course && course.duration && enrollmentData) {
          // Parse course total hours (e.g., "8 Hours" -> 8)
          const totalHoursMatch = course.duration.match(/(\d+)\s*Hours?/i);
          const totalHours = totalHoursMatch ? parseInt(totalHoursMatch[1], 10) : 4; // Default to 4 hours if parsing fails
          
          // Calculate actual session hours from time slot duration
          const sessionHours = selectedSchedule.timeSlotId 
            ? calculateTimeSlotDuration(selectedSchedule.timeSlotId)
            : (course.sessionHours || 4); // Fallback to course sessionHours or 4
          
          // Calculate current completed hours
          const currentCompletedHours = enrollmentData.completedHours || 0;
          const newCompletedHours = currentCompletedHours + sessionHours;
          
          // Check if course is now fully completed
          const isCourseCompleted = newCompletedHours >= totalHours;
          
          // Update enrollment with new completed hours and status
          const updateData: any = {
            completedHours: newCompletedHours,
            totalHours: totalHours
          };
          
          if (isCourseCompleted) {
            // Check if there's still an outstanding balance
            const balance = Number(enrollmentData.balance) || 0;
            if (balance > 0) {
              setError(`Course cannot be marked as completed. Outstanding balance: ₱${balance.toLocaleString()}. Please collect payment first.`);
              setLoading(false);
              return;
            }
            
            updateData.status = "completed";
            updateData.completedAt = new Date();
          }
          
          await updateDoc(doc(db, "enrollments", selectedSchedule.enrollmentId), updateData);
          
          // Create notification for student
          const student = students.find(s => s.id === selectedSchedule.studentId);
          const studentName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "Student";
          const courseName = course?.name || course?.title || "Course";

          if (isCourseCompleted) {
            // Course fully completed
            await addDoc(collection(db, "notifications"), {
              userId: selectedSchedule.studentId,
              type: "course_completed",
              title: "Course Completed!",
              message: `Congratulations! You have successfully completed ${courseName}. Total hours: ${newCompletedHours}/${totalHours}${completionNotes.trim() ? `. Notes: ${completionNotes.trim()}` : ""}`,
              read: false,
              createdAt: new Date(),
              relatedId: selectedSchedule.id
            });
          } else {
            // Session completed, but course still in progress
            await addDoc(collection(db, "notifications"), {
              userId: selectedSchedule.studentId,
              type: "session_completed",
              title: "Session Completed!",
              message: `Great job! You have completed a session for ${courseName}. Progress: ${newCompletedHours}/${totalHours} hours${completionNotes.trim() ? `. Notes: ${completionNotes.trim()}` : ""}`,
              read: false,
              createdAt: new Date(),
              relatedId: selectedSchedule.id
            });
          }
        }
      }

      // Real-time listeners will automatically update the UI
      setShowCompleteDialog(false);
      setSelectedSchedule(null);
      setCompletionNotes("");
    } catch (err) {
      console.error("Error completing schedule:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      setError(`Failed to complete schedule: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest || !assignedInstructor) {
      setError("Please select an instructor.");
      return;
    }
    
    setLoading(true);
    setError(null); // Clear previous errors
    
    try {
      console.log("Approving request:", selectedRequest);
      console.log("Assigned instructor:", assignedInstructor);
      
      // Determine which collection to update based on request type
      const collectionName = selectedRequest.type === "reschedule" ? "rescheduleRequests" : "scheduleRequests";
      
      // Update schedule/reschedule request
      await updateDoc(doc(db, collectionName, selectedRequest.id), {
        status: "approved",
        assignedInstructor,
        approvedAt: new Date()
      });

      // Get time display and date for notification and schedule
      let timeDisplay = "";
      let dateValue = "";
      let timeSlotId = "";
      
      if (selectedRequest.type === "schedule") {
        dateValue = selectedRequest.requestedDate || "";
        timeSlotId = selectedRequest.timeSlotId || "";
        if (selectedRequest.timeSlotId) {
          const timeSlot = timeSlots.find(slot => slot.id === selectedRequest.timeSlotId);
          if (timeSlot) {
            timeDisplay = `${formatTime12Hour(timeSlot.startTime)} - ${formatTime12Hour(timeSlot.endTime)}`;
          } else {
            console.warn("Time slot not found for ID:", selectedRequest.timeSlotId);
            timeDisplay = "Time slot not found";
          }
        }
      } else if (selectedRequest.type === "reschedule") {
        dateValue = selectedRequest.newPreferredDate || "";
        timeSlotId = selectedRequest.newTimeSlotId || "";
        if (selectedRequest.newTimeSlotId) {
          const timeSlot = timeSlots.find(slot => slot.id === selectedRequest.newTimeSlotId);
          if (timeSlot) {
            timeDisplay = `${formatTime12Hour(timeSlot.startTime)} - ${formatTime12Hour(timeSlot.endTime)}`;
          } else {
            console.warn("Time slot not found for ID:", selectedRequest.newTimeSlotId);
          }
        }
        // Fall back to stored time string if no time slot found
        if (!timeDisplay) {
          timeDisplay = selectedRequest.newPreferredTime || selectedRequest.preferredTime || "";
        }
      }

      console.log("Creating schedule with:", {
        dateValue,
        timeDisplay,
        timeSlotId,
        courseId: selectedRequest.courseId
      });

      // For reschedule requests, we might need to fetch the courseId from the original schedule
      let courseId = selectedRequest.courseId;
      if (!courseId && selectedRequest.type === "reschedule" && selectedRequest.scheduleId) {
        console.log("Fetching courseId from original schedule:", selectedRequest.scheduleId);
        try {
          const scheduleDoc = await getDoc(doc(db, "schedules", selectedRequest.scheduleId));
          if (scheduleDoc.exists()) {
            courseId = scheduleDoc.data().courseId;
            console.log("Found courseId from original schedule:", courseId);
          }
        } catch (fetchError) {
          console.warn("Failed to fetch original schedule:", fetchError);
        }
      }

      // Validate required fields
      if (!dateValue) {
        throw new Error("Date value is missing");
      }
      if (!timeDisplay) {
        throw new Error("Time display is missing");
      }
      if (!courseId) {
        throw new Error("Course ID is missing and could not be retrieved");
      }

      // Create schedule entry - only include fields with valid values
      const scheduleData: any = {
        studentId: selectedRequest.studentId,
        courseId: courseId, // Use the resolved courseId
        instructorId: assignedInstructor,
        date: dateValue,
        time: timeDisplay,
        status: "scheduled",
        type: selectedRequest.type,
        createdAt: new Date()
      };

      // Only add optional fields if they have valid values
      if (selectedRequest.enrollmentId) {
        scheduleData.enrollmentId = selectedRequest.enrollmentId;
      }
      if (selectedRequest.scheduleId) {
        scheduleData.scheduleId = selectedRequest.scheduleId;
      }
      if (timeSlotId) {
        scheduleData.timeSlotId = timeSlotId;
      }

      await addDoc(collection(db, "schedules"), scheduleData);

      // For reschedule requests, update the old schedule status to "rescheduled"
      if (selectedRequest.type === "reschedule" && selectedRequest.scheduleId) {
        console.log("Updating old schedule to rescheduled status:", selectedRequest.scheduleId);
        try {
          await updateDoc(doc(db, "schedules", selectedRequest.scheduleId), {
            status: "rescheduled",
            rescheduledAt: new Date(),
            newScheduleNote: `Rescheduled to ${dateValue} at ${timeDisplay}`
          });
          console.log("Successfully updated old schedule status");
        } catch (updateError) {
          console.error("Failed to update old schedule status:", updateError);
          // Continue with the process even if this fails
        }
      }

      // Get instructor name for notification
      const instructorDoc = await getDoc(doc(db, "users", assignedInstructor));
      const instructorData = instructorDoc.data();
      const instructorName = `${instructorData?.firstName || ""} ${instructorData?.lastName || ""}`.trim();

      // Create notification for student
      await addDoc(collection(db, "notifications"), {
        userId: selectedRequest.studentId,
        type: "schedule_approved",
        title: "Schedule Request Approved",
        message: `Your ${selectedRequest.type} request has been approved! Instructor: ${instructorName}, Date: ${dateValue}, Time: ${timeDisplay}`,
        read: false,
        createdAt: new Date()
      });

      // Real-time listeners will automatically update the UI
      setShowApproveDialog(false);
      setSelectedRequest(null);
      setAssignedInstructor("");
    } catch (err) {
      console.error("Error approving request:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      setError(`Failed to approve request: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      setError("Please provide a reason for rejection.");
      return;
    }
    
    setLoading(true);
    try {
      // Determine which collection to update based on request type
      const collectionName = selectedRequest.type === "reschedule" ? "rescheduleRequests" : "scheduleRequests";
      
      await updateDoc(doc(db, collectionName, selectedRequest.id), {
        status: "rejected",
        rejectionReason: rejectionReason.trim(),
        rejectedAt: new Date()
      });

      // Create notification for student
      await addDoc(collection(db, "notifications"), {
        userId: selectedRequest.studentId,
        type: "schedule_rejected",
        title: "Schedule Request Rejected",
        message: `Your ${selectedRequest.type} request has been rejected. Reason: ${rejectionReason.trim()}`,
        read: false,
        createdAt: new Date()
      });

      // Real-time listeners will automatically update the UI
      setShowRejectDialog(false);
      setSelectedRequest(null);
      setRejectionReason("");
    } catch (err) {
      console.error("Error rejecting request:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      setError(`Failed to reject request: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-blue-600 border-t-transparent"></div>
          <p className="mt-2">Loading schedule data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Schedule Management</h1>
        {branch && (
          <div className="text-sm text-muted-foreground">
            Branch: {branch}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="schedules">Active Schedules ({filteredSchedules.length})</TabsTrigger>
          <TabsTrigger value="requests">Schedule Requests ({filteredRequests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="schedules" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Active Schedules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6 flex-wrap items-end">
                <div className="flex flex-col flex-1 min-w-64">
                  <label className="mb-2 text-sm font-medium">Search</label>
                  <Input
                    placeholder="Search by student, instructor, course, or email..."
                    value={scheduleSearch}
                    onChange={(e) => setScheduleSearch(e.target.value)}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Status</label>
                  <Select value={scheduleStatus} onValueChange={setScheduleStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {scheduleStatusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Month</label>
                  <Select value={scheduleMonth} onValueChange={setScheduleMonth}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {format(new Date(2020, i, 1), "MMMM")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Year</label>
                  <Select value={scheduleYear} onValueChange={setScheduleYear}>
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {getYearOptions().map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredSchedules.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No schedules found matching your criteria.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left">Student</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Course</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Date</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Time</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Instructor</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSchedules.map((schedule) => {
                        const student = students.find((s) => s.id === schedule.studentId);
                        const course = courses.find((c) => c.id === schedule.courseId);
                        const instructor = instructors.find((i) => i.id === schedule.instructorId);

                        return (
                          <tr key={schedule.id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-2">
                              {student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "Unknown Student"}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              {course?.name || course?.title || "Unknown Course"}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{schedule.date}</td>
                            <td className="border border-gray-300 px-4 py-2">{schedule.time}</td>
                            <td className="border border-gray-300 px-4 py-2">
                              {instructor ? `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim() : "Not Assigned"}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                schedule.status === "scheduled" ? "bg-blue-100 text-blue-800" :
                                schedule.status === "completed" ? "bg-green-100 text-green-800" :
                                schedule.status === "cancelled" ? "bg-red-100 text-red-800" :
                                schedule.status === "rescheduled" ? "bg-yellow-100 text-yellow-800" :
                                "bg-gray-100 text-gray-800"
                              }`}>
                                {schedule.status}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              {schedule.status === "scheduled" && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedSchedule(schedule);
                                    setShowCompleteDialog(true);
                                  }}
                                  className="flex items-center gap-1"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                  Mark Complete
                                </Button>
                              )}
                              {schedule.status === "completed" && schedule.completionNotes && (
                                <span className="text-xs text-gray-500">
                                  Notes: {schedule.completionNotes}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarCheck className="h-5 w-5" />
                Schedule Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6 flex-wrap items-end">
                <div className="flex flex-col flex-1 min-w-64">
                  <label className="mb-2 text-sm font-medium">Search</label>
                  <Input
                    placeholder="Search by student, course, or email..."
                    value={requestSearch}
                    onChange={(e) => setRequestSearch(e.target.value)}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Status</label>
                  <Select value={requestStatus} onValueChange={setRequestStatus}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {requestStatusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Type</label>
                  <Select value={requestType} onValueChange={setRequestType}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {requestTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Month</label>
                  <Select value={requestMonth} onValueChange={setRequestMonth}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {format(new Date(2020, i, 1), "MMMM")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium">Year</label>
                  <Select value={requestYear} onValueChange={setRequestYear}>
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {getYearOptions().map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No schedule requests found matching your criteria.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left">Student</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Course</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Type</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Requested Date</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Preferred Time</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((request) => {
                        const student = students.find((s) => s.id === request.studentId);
                        const course = courses.find((c) => c.id === request.courseId);

                        // Get display values based on request type
                        const displayDate = request.type === "schedule" 
                          ? request.requestedDate 
                          : request.newPreferredDate;
                        
                        let displayTime = "";
                        if (request.type === "schedule" && request.timeSlotId) {
                          const timeSlot = timeSlots.find(slot => slot.id === request.timeSlotId);
                          if (timeSlot) {
                            displayTime = `${formatTime12Hour(timeSlot.startTime)} - ${formatTime12Hour(timeSlot.endTime)}`;
                          }
                        } else if (request.type === "reschedule") {
                          if (request.newTimeSlotId) {
                            const timeSlot = timeSlots.find(slot => slot.id === request.newTimeSlotId);
                            if (timeSlot) {
                              displayTime = `${formatTime12Hour(timeSlot.startTime)} - ${formatTime12Hour(timeSlot.endTime)}`;
                            }
                          } else {
                            displayTime = request.newPreferredTime || request.preferredTime || "";
                          }
                        }

                        return (
                          <tr key={request.id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-2">
                              {student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "Unknown Student"}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              {course?.name || course?.title || "Unknown Course"}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                request.type === "schedule" ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"
                              }`}>
                                {request.type}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-4 py-2">{displayDate}</td>
                            <td className="border border-gray-300 px-4 py-2">{displayTime}</td>
                            <td className="border border-gray-300 px-4 py-2">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                request.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                                request.status === "approved" ? "bg-green-100 text-green-800" :
                                request.status === "rejected" ? "bg-red-100 text-red-800" :
                                "bg-gray-100 text-gray-800"
                              }`}>
                                {request.status}
                              </span>
                              {request.status === "approved" && request.assignedInstructor && (
                                <div className="text-xs text-gray-600 mt-1">
                                  Instructor: {(() => {
                                    const instructor = instructors.find(i => i.id === request.assignedInstructor);
                                    return instructor ? `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim() : "Unknown";
                                  })()}
                                </div>
                              )}
                              {request.status === "rejected" && request.rejectionReason && (
                                <div className="text-xs text-red-600 mt-1">
                                  Reason: {request.rejectionReason}
                                </div>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              {request.status === "pending" && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setShowApproveDialog(true);
                                    }}
                                    className="flex items-center gap-1"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setShowRejectDialog(true);
                                    }}
                                    className="flex items-center gap-1"
                                  >
                                    <FileX className="h-4 w-4" />
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Complete Schedule Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Schedule as Complete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Mark this session as completed for{" "}
                {selectedSchedule && students.find(s => s.id === selectedSchedule.studentId) ? 
                  `${students.find(s => s.id === selectedSchedule.studentId)?.firstName || ""} ${students.find(s => s.id === selectedSchedule.studentId)?.lastName || ""}`.trim()
                  : "this student"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Completion Notes (Optional)</label>
              <Textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Add any notes about the completed session..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCompleteSchedule} disabled={loading}>
                {loading ? "Marking Complete..." : "Mark Complete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Request Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve {selectedRequest?.type === "reschedule" ? "Reschedule" : "Schedule"} Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Assign Instructor</label>
              <Select value={assignedInstructor} onValueChange={setAssignedInstructor}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an instructor" />
                </SelectTrigger>
                <SelectContent>
                  {instructors.map((instructor) => (
                    <SelectItem key={instructor.id} value={instructor.id}>
                      {`${instructor.firstName || ""} ${instructor.lastName || ""}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleApproveRequest} disabled={loading || !assignedInstructor}>
                {loading ? "Approving..." : "Approve"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Request Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject {selectedRequest?.type === "reschedule" ? "Reschedule" : "Schedule"} Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Rejection Reason</label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleRejectRequest} disabled={loading || !rejectionReason.trim()}>
                {loading ? "Rejecting..." : "Reject"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student Details Dialog */}
      <Dialog open={showStudentDetailsDialog} onOpenChange={setShowStudentDetailsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Student Details</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-2">
              <div><strong>Name:</strong> {`${selectedStudent.firstName || ""} ${selectedStudent.lastName || ""}`.trim()}</div>
              <div><strong>Email:</strong> {selectedStudent.email || "N/A"}</div>
              {/* Add more fields as needed, e.g. branch, enrollment type, course, status, etc. */}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowStudentDetailsDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffScheduleManagement;
